import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowLeft, Phone, MapPin, Globe, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIVE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '').replace('api/', '') || window.location.origin;

const BusinessCardPage = () => {
  const siteUrl = LIVE_URL;
  const qrUrl = `${API}/qrcode?url=${encodeURIComponent(siteUrl)}`;

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col" data-testid="business-card-page">
      <header className="border-b border-brand-border bg-brand-surface px-6 py-4 flex items-center justify-between">
        <Link to="/admin" className="font-serif text-xl text-brand-text">
          Afro <span className="text-brand-gold">Magic</span>
        </Link>
        <Link to="/admin" className="flex items-center gap-1 font-sans text-xs text-brand-text-muted hover:text-brand-gold"><ArrowLeft size={14} /> Admin</Link>
      </header>

      <div className="flex-1 px-6 py-12 max-w-4xl mx-auto w-full">
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl text-brand-text mb-2">QR Code & Business Card</h1>
          <p className="font-sans text-sm text-brand-text-muted">Print these for your shop, hand out to clients, or share on social media</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* QR Code */}
          <div className="border border-brand-border p-6 text-center" data-testid="qr-code-section">
            <h2 className="font-serif text-xl text-brand-text mb-4">Booking QR Code</h2>
            <p className="font-sans text-xs text-brand-text-muted mb-4">Clients scan this to go directly to your booking page</p>
            <div className="bg-brand-surface border border-brand-border p-6 inline-block mb-4">
              <img src={qrUrl} alt="QR Code to booking page" className="w-48 h-48 bg-white p-3" data-testid="qr-code-image" />
            </div>
            <p className="font-sans text-[10px] text-brand-text-muted mb-4 break-all">{siteUrl}</p>
            <a href={qrUrl} download="afro-magic-qr-code.png" className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold-light transition-colors" data-testid="download-qr-btn">
              <Download size={14} /> Download QR Code
            </a>
          </div>

          {/* Business Card Preview */}
          <div className="border border-brand-border p-6" data-testid="business-card-section">
            <h2 className="font-serif text-xl text-brand-text mb-4 text-center">Business Card</h2>
            <p className="font-sans text-xs text-brand-text-muted mb-4 text-center">Print-ready design for your business cards</p>

            {/* Card Front */}
            <div className="bg-[#0F0C09] border-2 border-brand-gold/30 p-6 mb-4 aspect-[3.5/2] flex flex-col justify-between relative overflow-hidden" data-testid="card-front">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gold/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="flex items-center gap-3">
                <img src="/logos/v3_circle_crest.png" alt="Afro Magic crest" className="h-14 w-14 object-contain flex-shrink-0" />
                <div>
                  <h3 className="font-serif text-2xl text-brand-gold font-semibold leading-tight">Afro Magic</h3>
                  <p className="font-serif text-lg text-brand-text leading-tight">Braiding Hair</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <p className="font-sans text-[11px] text-brand-text-muted flex items-center gap-2">
                  <MapPin size={10} className="text-brand-gold flex-shrink-0" /> 22560 Telegraph Rd, Southfield, MI 48033
                </p>
                <p className="font-sans text-[11px] text-brand-text-muted flex items-center gap-2">
                  <Phone size={10} className="text-brand-gold flex-shrink-0" /> (313) 484-7307
                </p>
                <p className="font-sans text-[11px] text-brand-text-muted flex items-center gap-2">
                  <Mail size={10} className="text-brand-gold flex-shrink-0" /> booking@afromagicbraiding.shop
                </p>
                <p className="font-sans text-[11px] text-brand-text-muted flex items-center gap-2">
                  <Globe size={10} className="text-brand-gold flex-shrink-0" /> afromagicbraiding.shop
                </p>
              </div>
            </div>

            {/* Card Back */}
            <div className="bg-[#0F0C09] border-2 border-brand-gold/30 p-6 aspect-[3.5/2] flex flex-col items-center justify-center text-center" data-testid="card-back">
              <img src="/logos/v3_circle_crest.png" alt="Afro Magic crest" className="h-12 w-12 object-contain mb-2" />
              <h3 className="font-serif text-xl text-brand-gold font-semibold mb-1">Afro Magic Braiding Hair</h3>
              <p className="font-sans text-[10px] text-brand-text-muted tracking-[0.2em] uppercase mb-4">Where Every Braid is Magic</p>
              <div className="bg-brand-surface border-2 border-brand-gold p-3 mb-3">
                <img src={qrUrl} alt="QR" className="w-32 h-32 bg-white p-2" />
              </div>
              <p className="font-sans text-xs text-brand-gold font-semibold">Scan to View Our Work & Book Online</p>
            </div>
          </div>
        </div>

        {/* Logo Pack — Brand Assets */}
        <div className="mt-8 border border-brand-border bg-brand-surface p-5" data-testid="logo-pack">
          <h3 className="font-serif text-lg text-brand-text mb-1">Logo Pack — Brand Assets</h3>
          <p className="font-sans text-xs text-brand-text-muted mb-4">
            Right-click any logo and choose "Save Image As" to download. Use them on social media, packaging, signage, etc.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { src: "/logos/v1_emblem.png", name: "Primary Logo", use: "Website header, main sign, posters" },
              { src: "/logos/v2_monogram.png", name: "Monogram Stamp", use: "Social media graphics, product packaging" },
              { src: "/logos/v3_circle_crest.png", name: "Circle Crest", use: "Business cards, website footer, favicon" },
            ].map((l) => (
              <div key={l.src} className="bg-[#0F0C09] border border-brand-gold/30 p-4 flex flex-col items-center text-center">
                <img src={l.src} alt={l.name} className="w-32 h-32 object-contain mb-3" />
                <p className="font-sans text-xs text-brand-gold font-semibold uppercase tracking-wider mb-1">{l.name}</p>
                <p className="font-sans text-[11px] text-brand-text-muted mb-3">{l.use}</p>
                <a
                  href={l.src}
                  download
                  className="font-sans text-[11px] text-brand-gold border border-brand-gold/40 px-3 py-1 hover:bg-brand-gold/10 transition-colors w-full"
                  data-testid={`download-${l.src.split('/').pop().replace('.png','')}`}
                >
                  Download PNG
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Print Tips */}
        <div className="mt-8 border border-brand-border bg-brand-surface p-5" data-testid="print-tips">
          <h3 className="font-serif text-lg text-brand-text mb-3">Print Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="font-sans text-xs text-brand-gold font-semibold mb-1">QR Code Uses</p>
              <ul className="font-sans text-xs text-brand-text-muted space-y-1">
                <li>Print on business cards</li>
                <li>Post at your shop entrance</li>
                <li>Add to flyers and posters</li>
                <li>Share on Instagram stories</li>
              </ul>
            </div>
            <div>
              <p className="font-sans text-xs text-brand-gold font-semibold mb-1">Business Card Printing</p>
              <ul className="font-sans text-xs text-brand-text-muted space-y-1">
                <li>Use Vistaprint or Canva Print</li>
                <li>Standard size: 3.5" x 2"</li>
                <li>Matte black cardstock recommended</li>
                <li>Gold foil for premium look</li>
              </ul>
            </div>
            <div>
              <p className="font-sans text-xs text-brand-gold font-semibold mb-1">Shop Poster</p>
              <ul className="font-sans text-xs text-brand-text-muted space-y-1">
                <li>Print QR code large (8x8 inches)</li>
                <li>Add text: "Scan to Book Online"</li>
                <li>Place near reception/mirrors</li>
                <li>Include on table cards</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessCardPage;
